// Shared setup for the site's Chart.js charts (profile-chart.js,
// score-distribution-chart.js). Each chart's data is rendered into its
// container with json_script.

import { onElements } from './dom.js'
import { prefersReducedMotion } from './preferences.js'

// Chart.js draws on a canvas, so it can't use CSS variables directly. Read
// their values instead.
const themeColours = () => {
    const styles = getComputedStyle(document.documentElement)
    const value = (name) => styles.getPropertyValue(name).trim()
    return {
        text: value('--text'),
        background: value('--background'),
        primary: value('--primary'),
        grid: value('--secondary'),
    }
}

const animationOption = () => (prefersReducedMotion() ? false : {})

// Resolves once `element` has a size, e.g. when the hidden modal it's in opens.
const whenVisible = (element) =>
    new Promise((resolve) => {
        if (element.clientWidth) return resolve()
        const observer = new ResizeObserver(() => {
            if (!element.clientWidth) return
            observer.disconnect()
            resolve()
        })
        observer.observe(element)
    })

const drawChart = async (container, build) => {
    if (container.dataset.chartInitialized) return
    container.dataset.chartInitialized = 'true'

    // Loaded on demand so Chart.js only ships to pages with a chart. A chart
    // in a hidden container (e.g. the finish modal) waits until it's shown, so
    // it animates in at its real size rather than resizing mid-animation.
    const [{ Chart, color }] = await Promise.all([
        import('./lazy/chart.js'),
        whenVisible(container),
    ])

    const colours = themeColours()
    const colour = (name, alpha = 1) =>
        alpha === 1
            ? colours[name]
            : color(colours[name]).alpha(alpha).rgbString()

    const chart = build({
        Chart,
        canvas: container.querySelector('canvas'),
        data: JSON.parse(container.querySelector('script').textContent),
        colour,
        animation: animationOption(),
    })

    const onPreferenceChange = () => {
        // htmx swapped the chart out (e.g. the finish modal re-rendering)
        if (!chart.canvas.isConnected) {
            document.removeEventListener('preferencechange', onPreferenceChange)
            chart.destroy()
            return
        }
        chart.options.animation = animationOption()
        chart.update('none')
    }
    document.addEventListener('preferencechange', onPreferenceChange)
}

/**
 * Draws a chart into every element matching `selector`: those on the page
 * and any htmx adds later (e.g. the finish modal). `build({ Chart, canvas,
 * data, colour, animation })` returns the Chart. `colour(name, alpha)` gives
 * a theme colour (text, background, primary or grid), optionally made
 * translucent.
 */
export const setupCharts = (selector, build) =>
    onElements(selector, (container) => drawChart(container, build))
